import React from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, Bot, Euro, Layers, Truck, Undo2, UserRound, Users, Warehouse } from 'lucide-react';
import { displayLocale } from '../utils/dates';
import { getStatusLabel } from '../constants/ticketStatuses';
import './DashboardPipeline.css';

// Every open ticket by stage of its case — GET /api/tickets/dashboard-insights
// `pipeline` (Kevin 29/10: the status tiles covered 4 statuses out of 20).
// One card per stage the API returned, in the order of a case (Kevin 05/11:
// « transforme le tableau en cards » — the table took half the screen). A
// card filters the list on the stage's statuses; its dormant count (status
// unchanged for more than `dormant_after_days`), on those dormant tickets.

const STAGE_DEFAULTS = {
  team: ['Votre équipe', 'Nouveaux, à traiter, affectés : la prochaine action est chez vous.'],
  carrier: ['Transporteur', 'En attente d’une réponse du transporteur ou litige en cours.'],
  client: ['Client', 'En attente d’une réponse ou de pièces du client.'],
  warehouse: ['Entrepôt', 'En attente d’une réponse de l’entrepôt.'],
  compensation: ['Indemnisation', 'À indemniser, indemnisation en cours ou facturée.'],
  npec: ['NPEC', 'Non pris en charge, en attente de clôture.'],
  return: ['Retour', 'Colis en cours de retour.'],
  ai: ['IA', 'Traités automatiquement par l’agent IA.'],
  other: ['Autres statuts', 'Statuts hors étapes : leur nom est listé.'],
};

const STAGE_ICONS = {
  team: Users,
  carrier: Truck,
  client: UserRound,
  warehouse: Warehouse,
  compensation: Euro,
  npec: Ban,
  return: Undo2,
  ai: Bot,
  other: Layers,
};

export const stageLabel = (t, key) => t(`dashPipelineUi.stage.${key}`, (STAGE_DEFAULTS[key] || [key])[0]);
const stageHint = (t, key) => t(`dashPipelineUi.stageHint.${key}`, (STAGE_DEFAULTS[key] || ['', ''])[1]);

export const stageStatuses = (stage) => (stage.statuses || []).map((row) => row.status);

const sameSet = (a, b) => a.length === b.length && a.every((value) => b.includes(value));

const DashboardPipeline = ({ pipeline, activeStatuses = [], dormantActive = false, onSelect }) => {
  const { t, i18n } = useTranslation();
  const locale = displayLocale(i18n.language);
  // A plain no-break space between thousands: the narrow one French formatting
  // uses nearly vanishes in small text (same choice as DashboardInsights).
  const number = (value) => new Intl.NumberFormat(locale).format(value).replace(/ /g, ' ');

  if (pipeline === undefined) {
    return (
      <div className="u-skeleton dash-pipe__skeleton" role="status" aria-busy="true"
        aria-label={t('dashPipelineUi.loading', 'Chargement des étapes')} />
    );
  }
  if (!pipeline || !Array.isArray(pipeline.stages) || pipeline.stages.length === 0) return null;

  const days = pipeline.dormant_after_days;
  const allStatuses = pipeline.stages.flatMap(stageStatuses);
  const select = (statuses, dormant) => { if (onSelect) onSelect({ statuses, dormant }); };
  const title = t('dashPipelineUi.title', 'Tickets ouverts par étape');

  return (
    <section className="dash-pipe" aria-labelledby="dash-pipe-title">
      <div className="dash-pipe__head">
        <span className="u-overline" id="dash-pipe-title">{title}</span>
        <span className="dash-pipe__total">
          {t('dashPipelineUi.total', '{{formatted}} tickets ouverts', {
            count: pipeline.total, formatted: number(pipeline.total),
          })}
        </span>
        {pipeline.dormant_total > 0 && (
          <button
            type="button"
            className="u-link dash-pipe__link"
            aria-pressed={dormantActive && sameSet(activeStatuses, allStatuses)}
            onClick={() => select(allStatuses, true)}
          >
            {t('dashPipelineUi.dormantTotal', '{{formatted}} sans changement de statut depuis plus de {{days}} jours', {
              count: pipeline.dormant_total, formatted: number(pipeline.dormant_total), days,
            })}
          </button>
        )}
      </div>
      <div className="dash-pipe__cards" role="group" aria-label={title}>
        {pipeline.stages.map((stage) => {
          const statuses = stageStatuses(stage);
          const active = sameSet(activeStatuses, statuses);
          const label = stageLabel(t, stage.key);
          const Icon = STAGE_ICONS[stage.key] || Layers;
          const detail = (stage.statuses || [])
            .map((row) => `${getStatusLabel(t, row.status)} ${number(row.count)}`)
            .join(' · ');
          const median = typeof stage.median_days_in_status === 'number'
            ? t('dashPipelineUi.cardMedian', 'médiane {{count}} j', { count: Math.round(stage.median_days_in_status) })
            : null;
          return (
            <div key={stage.key} className={`dash-pipe__card${active ? ' is-active' : ''}`}>
              <button
                type="button"
                className="dash-pipe__card-main"
                aria-pressed={active && !dormantActive}
                onClick={() => select(active && !dormantActive ? [] : statuses, false)}
                title={`${stageHint(t, stage.key)}\n${detail}`.trim()}
              >
                <span className="dash-pipe__card-name">
                  <span className="u-icon-chip u-icon-chip--sm dash-pipe__card-icon" aria-hidden="true"><Icon strokeWidth={1.8} /></span>
                  <span className="dash-pipe__card-label">{label}</span>
                </span>
                <span className="dash-pipe__card-count">{number(stage.count)}</span>
              </button>
              <span className="dash-pipe__card-context">
                {stage.dormant > 0 ? (
                  <button
                    type="button"
                    className="u-link dash-pipe__link"
                    aria-pressed={active && dormantActive}
                    onClick={() => select(statuses, true)}
                    aria-label={t('dashPipelineUi.dormantRow', '{{label}} : {{formatted}} tickets sans changement de statut depuis plus de {{days}} jours', {
                      label, count: stage.dormant, formatted: number(stage.dormant), days,
                    })}
                  >
                    {t('dashPipelineUi.cardDormant', '{{formatted}} inchangés > {{days}} j', {
                      count: stage.dormant, formatted: number(stage.dormant), days,
                    })}
                  </button>
                ) : (
                  <span>{t('dashPipelineUi.cardNoDormant', '0 inchangé > {{days}} j', { days })}</span>
                )}
                {median && (
                  <span title={t('dashPipelineUi.cardMedianHint', 'Temps médian depuis le dernier changement de statut')}>{median}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default DashboardPipeline;
